/**
 * USGS 3DEP — 1 m / 10 m over the United States.
 *
 * The ImageServer `exportImage` endpoint serves an arbitrary bbox on demand as
 * float32 GeoTIFF, which avoids any tiling logic. It is requested in EPSG:4326
 * and resampled to the metric working grid here, exactly as the tiled providers
 * are, so nothing downstream can tell the difference.
 */

import { fromArrayBuffer } from 'geotiff';
import type { DemProvider, DemRequest, HeightGrid } from '../types';
import { NoCoverageError, REGIONS } from '../types';
import { gridToLonLat, type MetricDomain } from '../projection';

const ENDPOINT =
  'https://elevation.nationalmap.gov/arcgis/rest/services/3DEPElevation/ImageServer/exportImage';

const MAX_PIXELS = 2048;

export const usgs3depProvider: DemProvider = {
  info: {
    id: 'usgs-3dep',
    label: 'USGS 3DEP',
    nativeResolution: 10,
    attribution: 'Elevation: USGS 3DEP (public domain)',
    covers: REGIONS.usa,
  },

  async load(req: DemRequest, domain: MetricDomain): Promise<HeightGrid> {
    const b = domain.bbox;
    // Oversample slightly so the resample to the metric grid is not aliased.
    const px = Math.min(MAX_PIXELS, Math.round(domain.gridSize * 1.3));
    // Match the raster aspect to the bbox aspect so pixels stay near-square.
    const aspect = (b[2] - b[0]) / Math.max(1e-9, b[3] - b[1]);
    const width = px;
    const height = Math.max(16, Math.min(MAX_PIXELS, Math.round(px / Math.max(0.2, aspect))));

    const url =
      `${ENDPOINT}?bbox=${b[0]},${b[1]},${b[2]},${b[3]}` +
      `&bboxSR=4326&imageSR=4326&size=${width},${height}` +
      `&format=tiff&pixelType=F32&interpolation=RSP_BilinearInterpolation&f=image`;

    const res = await fetch(url, { signal: req.signal });
    if (!res.ok) throw new NoCoverageError('usgs-3dep');
    const buf = await res.arrayBuffer();
    if (buf.byteLength < 1024) throw new NoCoverageError('usgs-3dep');

    const tiff = await fromArrayBuffer(buf);
    const image = await tiff.getImage();
    const rasters = await image.readRasters({ interleave: true });
    const src = rasters as unknown as Float32Array;
    const rw = image.getWidth();
    const rh = image.getHeight();

    // 3DEP returns a large negative sentinel outside coverage.
    let valid = 0;
    for (let i = 0; i < src.length; i += 97) if (src[i] > -1000 && src[i] < 9000) valid++;
    if (valid === 0) throw new NoCoverageError('usgs-3dep');

    const { gridSize } = domain;
    const data = new Float32Array(gridSize * gridSize);
    for (let j = 0; j < gridSize; j++) {
      for (let i = 0; i < gridSize; i++) {
        const [lon, lat] = gridToLonLat(domain, i, j);
        // Raster row 0 is the north edge.
        const fx = ((lon - b[0]) / (b[2] - b[0])) * rw - 0.5;
        const fy = ((b[3] - lat) / (b[3] - b[1])) * rh - 0.5;
        data[j * gridSize + i] = bilinear(src, rw, rh, fx, fy);
      }
    }

    return {
      data,
      width: gridSize,
      height: gridSize,
      cellSize: domain.cellSize,
      origin: domain.origin,
      crs: domain.frame.crs,
      source: '3DEP',
    };
  },
};

function bilinear(src: Float32Array, w: number, h: number, fx: number, fy: number): number {
  const x0 = Math.min(w - 1, Math.max(0, Math.floor(fx)));
  const y0 = Math.min(h - 1, Math.max(0, Math.floor(fy)));
  const x1 = Math.min(w - 1, x0 + 1);
  const y1 = Math.min(h - 1, y0 + 1);
  const tx = Math.min(1, Math.max(0, fx - x0));
  const ty = Math.min(1, Math.max(0, fy - y0));
  const clean = (v: number) => (v > -1000 && v < 9000 ? v : 0);
  return (
    clean(src[y0 * w + x0]) * (1 - tx) * (1 - ty) +
    clean(src[y0 * w + x1]) * tx * (1 - ty) +
    clean(src[y1 * w + x0]) * (1 - tx) * ty +
    clean(src[y1 * w + x1]) * tx * ty
  );
}
